'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import clsx from 'clsx';
import { useEmailSender } from '@gjl/lib';
import dynamic from 'next/dynamic';
const CalendlyWidget = dynamic(() => import('./calendly-widget'), { ssr: false });
export function ContactForm({ calendlyUrl, calendlyHeading = 'Or book a free strategy call', header, body, to = 'garrett@garrettjohnlaw.com', subject, handleSubmit, className, buttonVariant, buttonClassName, renderSuccessMessage, }) {
    const [form, setForm] = useState({ name: '', email: '', message: '', honeypot: '' });
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sendError, setSendError] = useState(null);
    const { sendEmail } = useEmailSender({
        onSuccess: () => {
            setSubmitted(true);
            setIsSubmitting(false);
        },
        onError: () => {
            setSendError('Failed to send message. Please try again.');
            setIsSubmitting(false);
        },
    });
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };
    const onSubmit = (e) => {
        e.preventDefault();
        setSendError(null);
        setIsSubmitting(true);
        if (handleSubmit) {
            handleSubmit(form);
            setSubmitted(true);
            setIsSubmitting(false);
        }
        else {
            sendEmail({
                to,
                subject: subject ?? `New message from ${form.name}`,
                text: `From: ${form.email}\n\n${form.message}`,
                nickname: form.honeypot,
            });
        }
    };
    return (_jsx("main", { className: clsx('bg-transparent py-24', className), children: _jsxs("div", { className: "mx-auto max-w-xl space-y-8 px-4", children: [_jsxs("section", { className: "space-y-2 text-center", children: [_jsx("p", { className: "text-4xl font-bold tracking-tight", children: header }), _jsx(Separator, { className: "bg-border mx-auto h-[2px] w-16" }), _jsx("p", { children: body })] }), submitted ? (renderSuccessMessage ? (renderSuccessMessage()) : (_jsx("div", { className: "text-center text-lg font-medium text-green-600", children: "\u2705 Message sent successfully!" }))) : (_jsxs("form", { onSubmit: onSubmit, className: "space-y-4", children: [_jsx(InputField, { id: "name", label: "Name", value: form.name, onChange: handleChange, disabled: isSubmitting }), _jsx(InputField, { id: "email", label: "Email", type: "email", value: form.email, onChange: handleChange, disabled: isSubmitting }), _jsx(TextareaField, { id: "message", label: "Message", value: form.message, onChange: handleChange, disabled: isSubmitting }), _jsx("label", { htmlFor: "honeypot", className: "sr-only", children: "Leave this field empty" }), _jsx("input", { type: "text", name: "honeypot", value: form.honeypot, onChange: handleChange, tabIndex: -1, autoComplete: "off", className: "pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0" }), _jsx(Button, { type: "submit", variant: buttonVariant ?? 'default', className: clsx('w-full', buttonClassName), disabled: isSubmitting, children: isSubmitting ? 'Sending...' : 'Send Message' }), sendError && (_jsx("p", { role: "alert", className: "text-center text-sm font-medium text-red-600", children: sendError }))] })), calendlyUrl && (_jsxs("div", { className: "pt-12", children: [_jsx("h2", { className: "mb-4 text-center text-xl font-semibold", children: calendlyHeading }), _jsx(CalendlyWidget, { calendlyUrl: calendlyUrl })] }))] }) }));
}
function InputField({ id, label, type = 'text', value, onChange, disabled, }) {
    return (_jsxs("div", { children: [_jsx("label", { htmlFor: id, className: "mb-1 block text-sm font-medium", children: label }), _jsx(Input, { id: id, name: id, type: type, value: value, onChange: onChange, required: true, disabled: disabled })] }));
}
function TextareaField({ id, label, value, onChange, disabled, }) {
    return (_jsxs("div", { children: [_jsx("label", { htmlFor: id, className: "mb-1 block text-sm font-medium", children: label }), _jsx(Textarea, { id: id, name: id, rows: 5, value: value, onChange: onChange, required: true, disabled: disabled })] }));
}
